/**
 * Configuración y Estado de Asistentes
 * Guarda y recupera parámetros del LocalStorage, sincronizando con el servidor en modo remoto.
 * Cumple con .idx-rules.md: funciones cortas y desacopladas.
 */

const CONFIG_KEY = 'dni_reader_config';
const CAPACITY_KEY = 'dni_reader_capacity';

// Auto-detectar ruta base del servidor para api.php
const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

let relativeApiPath = 'mysql-backend/api.php';
let currentPath = window.location.pathname;
if (currentPath.endsWith('.html')) {
  currentPath = currentPath.substring(0, currentPath.lastIndexOf('/'));
}
if (!currentPath.endsWith('/')) {
  currentPath += '/';
}
const detectedApiUrl = `${window.location.origin}${currentPath}${relativeApiPath}`;

const DEFAULT_CONFIG = {
  secretWord: 'SECRET_DNI_PERU_2026',
  frequentThreshold: 3,
  maxCapacity: 100,
  dbMode: isLocalhost ? 'local' : 'mysql',
  apiUrl: isLocalhost ? 'http://localhost/api-lector-dni/api.php' : detectedApiUrl,
  showLimit: true
};

/**
 * Obtiene la configuración guardada localmente o retorna los valores por defecto.
 */
export function getConfig() {
  try {
    const raw = localStorage.getItem(CONFIG_KEY);
    const config = raw ? JSON.parse(raw) : null;
    const isLocal = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';

    if (!config) {
      return { ...DEFAULT_CONFIG };
    }

    // Auto-corrección: si está en un hosting pero la configuración quedó apuntando a local/localhost
    if (!isLocal && (config.dbMode === 'local' || (config.apiUrl && config.apiUrl.includes('localhost')))) {
      config.dbMode = 'mysql';
      config.apiUrl = DEFAULT_CONFIG.apiUrl;
      localStorage.setItem(CONFIG_KEY, JSON.stringify(config));
    }

    return config;
  } catch (error) {
    console.error('Error al leer la configuración:', error);
    return { ...DEFAULT_CONFIG };
  }
}

/**
 * Guarda los parámetros de configuración localmente.
 * @param {Object} newConfig
 */
export function saveConfig(newConfig) {
  try {
    const merged = { ...getConfig(), ...newConfig };
    localStorage.setItem(CONFIG_KEY, JSON.stringify(merged));
    return true;
  } catch (error) {
    console.error('Error al guardar la configuración:', error);
    return false;
  }
}

/**
 * Sincroniza la configuración desde el servidor remoto y la guarda localmente.
 * Solo aplica cuando dbMode='mysql' y hay apiUrl configurada.
 * Razón: Garantiza que todos los dispositivos compartan la misma configuración.
 * @param {number|null} companyId - ID de empresa del usuario actual
 * @returns {Promise<boolean>} true si se sincronizó exitosamente
 */
export async function syncConfigFromServer(companyId) {
  const localCfg = getConfig();
  if (localCfg.dbMode !== 'mysql' || !localCfg.apiUrl) return false;

  try {
    const url = `${localCfg.apiUrl}?action=get_config&company_id=${companyId ?? ''}`;
    const res = await fetch(url, { method: 'GET' });
    if (!res.ok) return false;

    const remote = await res.json();
    if (remote && !remote.error && typeof remote === 'object' && Object.keys(remote).length > 0) {
      // Mezclar: dbMode y apiUrl se mantienen locales (son por dispositivo para bootstrapping)
      const merged = { ...localCfg, ...remote, dbMode: localCfg.dbMode, apiUrl: localCfg.apiUrl };
      localStorage.setItem(CONFIG_KEY, JSON.stringify(merged));
      return true;
    }
    return false;
  } catch (err) {
    console.warn('No se pudo sincronizar configuración remota:', err);
    return false;
  }
}

/**
 * Guarda la configuración tanto localmente como en el servidor remoto (si aplica).
 * @param {Object} newConfig
 * @param {number|null} companyId
 */
export async function saveConfigRemote(newConfig, companyId) {
  saveConfig(newConfig);
  const cfg = getConfig();
  if (cfg.dbMode !== 'mysql' || !cfg.apiUrl) return;

  try {
    const payload = { ...newConfig, company_id: companyId ?? null };
    // No guardar dbMode ni apiUrl en servidor (son parámetros de arranque por dispositivo)
    delete payload.dbMode;
    delete payload.apiUrl;
    await fetch(`${cfg.apiUrl}?action=save_config`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
  } catch (err) {
    console.warn('No se pudo guardar configuración remota:', err);
  }
}

/**
 * Obtiene la cantidad de asistentes actual.
 */
export function getCapacity() {
  try {
    const value = localStorage.getItem(CAPACITY_KEY);
    return value ? parseInt(value, 10) : 0;
  } catch (error) {
    console.error('Error al leer la cantidad de asistentes:', error);
    return 0;
  }
}

/**
 * Modifica la cantidad de asistentes actual directamente.
 * @param {number} count
 */
export function setCapacity(count) {
  try {
    const cleanCount = Math.max(0, count);
    localStorage.setItem(CAPACITY_KEY, cleanCount.toString());
    window.dispatchEvent(new Event('capacity_changed'));
    return cleanCount;
  } catch (error) {
    console.error('Error al actualizar asistentes:', error);
    return 0;
  }
}

/**
 * Incrementa la cantidad de asistentes actual si no excede el límite máximo.
 */
export function incrementCapacity() {
  const current = getCapacity();
  const { maxCapacity } = getConfig();
  if (current >= maxCapacity) {
    return { success: false, current };
  }
  const nextVal = setCapacity(current + 1);
  return { success: true, current: nextVal };
}

/**
 * Decrementa la cantidad de asistentes actual.
 */
export function decrementCapacity() {
  const current = getCapacity();
  const nextVal = setCapacity(current - 1);
  return { success: true, current: nextVal };
}
